import type { GameState, LadderRow, LeagueFixtureResult, MatchResult, ScheduledFixture, SeasonState, StaffPlan, TeamId } from './types'
import { OPPONENT_PROFILES, SEASON_SEED, TEAMS, USER_TEAM_ID } from './seasonData'
import { COMPETITION_CLUBS, advanceFinals, createFinalsState, generateRegularSeasonFixture } from './competition'
import { hydrateSeasonSimulation } from './matchContext'
import { repairUnavailableSelections } from './injuries'
import { createResearchPlan } from './research'

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

export const createSeasonState = (_includePreplayedResults = false, seasonNumber = 1, careerSeed = 'trial-career'): SeasonState => ({
  season: seasonNumber,
  seed: SEASON_SEED + ((seasonNumber - 1) * 1000),
  careerSeed,
  userTeamId: USER_TEAM_ID,
  completedThroughRound: 0,
  teams: clone(TEAMS),
  fixtures: generateRegularSeasonFixture(COMPETITION_CLUBS, seasonNumber),
  results: {},
  matchContexts: {},
  processedByeRounds: [],
})

export const initialiseSeason = (state: GameState): GameState => {
  if (state.season && state.week) return { ...state, season: hydrateSeasonSimulation(state.season) }
  const season = hydrateSeasonSimulation(state.season ?? createSeasonState())
  const fixture = fixtureForTeamOrBye(season, USER_TEAM_ID, state.round)
  return {
    ...state,
    season,
    week: state.week ?? { fixtureId: fixture?.id ?? `s${season.season}-r${state.round}-bye-${USER_TEAM_ID}`, venue: fixture ? (fixture.homeTeamId === USER_TEAM_ID ? 'home' : 'away') : 'bye', staffDefaults: clone(state.staffPlan) },
  }
}

export const fixtureForTeamOrBye = (season: SeasonState, teamId: TeamId, round: number): ScheduledFixture | undefined =>
  season.fixtures.find(entry => entry.round === round && (entry.homeTeamId === teamId || entry.awayTeamId === teamId))

export const fixtureForTeam = (season: SeasonState, teamId: TeamId, round: number): ScheduledFixture => {
  const fixture = fixtureForTeamOrBye(season, teamId, round)
  if (!fixture) throw new Error(`No fixture for ${teamId} in Round ${round}`)
  return fixture
}

const hash = (value: string) => [...value].reduce((total, char) => ((total * 31) + char.charCodeAt(0)) >>> 0, 2166136261)
const score = (goals: number, behinds: number) => ({ goals, behinds, total: goals * 6 + behinds })

export const simulateLeagueFixture = (season: SeasonState, fixture: ScheduledFixture): LeagueFixtureResult => {
  const home = season.teams[fixture.homeTeamId]
  const away = season.teams[fixture.awayTeamId]
  const homeRoll = hash(`${season.seed}:${fixture.id}:home`) % 19
  const awayRoll = hash(`${season.seed}:${fixture.id}:away`) % 19
  const homeGoals = Math.max(5, 9 + Math.round((home.simulationRating - 70) / 8) + Math.floor(homeRoll / 5))
  const awayGoals = Math.max(5, 8 + Math.round((away.simulationRating - 70) / 8) + Math.floor(awayRoll / 5))
  return {
    fixtureId: fixture.id,
    round: fixture.round,
    homeTeamId: fixture.homeTeamId,
    awayTeamId: fixture.awayTeamId,
    homeScore: score(homeGoals, 6 + (homeRoll % 7)),
    awayScore: score(awayGoals, 6 + (awayRoll % 7)),
  }
}

export const recordLeagueRound = (rawState: GameState, userResult: MatchResult): GameState => {
  const state = initialiseSeason(rawState)
  const season = state.season!
  const fixtures = season.fixtures.filter(entry => entry.round === state.round)
  const results = { ...season.results }
  for (const fixture of fixtures) {
    if (results[fixture.id]) continue
    if (fixture.homeTeamId === season.userTeamId || fixture.awayTeamId === season.userTeamId) {
      const home = fixture.homeTeamId === season.userTeamId
      results[fixture.id] = {
        fixtureId: fixture.id,
        round: fixture.round,
        homeTeamId: fixture.homeTeamId,
        awayTeamId: fixture.awayTeamId,
        homeScore: clone(home ? userResult.ourScore : userResult.opponentScore),
        awayScore: clone(home ? userResult.opponentScore : userResult.ourScore),
      }
    } else results[fixture.id] = simulateLeagueFixture(season, fixture)
  }
  return { ...state, season: { ...season, results } }
}

export const recordAiRound = (season: SeasonState, round: number): SeasonState => {
  const results = { ...season.results }
  for (const fixture of season.fixtures.filter(entry => entry.round === round)) {
    if (!results[fixture.id]) results[fixture.id] = simulateLeagueFixture(season, fixture)
  }
  return { ...season, results }
}

export const recordFinalsResult = (season: SeasonState, fixtureId: string, userResult: MatchResult): SeasonState => {
  const finals = season.finals
  const fixture = finals?.fixtures.find(entry => entry.id === fixtureId)
  if (!finals || !fixture || fixture.result) return season
  const home = fixture.homeTeamId === season.userTeamId
  const result: LeagueFixtureResult = {
    fixtureId: fixture.id,
    round: 19 + fixture.week,
    homeTeamId: fixture.homeTeamId,
    awayTeamId: fixture.awayTeamId,
    homeScore: clone(home ? userResult.ourScore : userResult.opponentScore),
    awayScore: clone(home ? userResult.opponentScore : userResult.ourScore),
  }
  return { ...season, finals: { ...finals, fixtures: finals.fixtures.map(entry => entry.id === fixture.id ? { ...entry, result } : entry) } }
}

export const completeSeasonFinals = (season: SeasonState): SeasonState => {
  if (season.completedThroughRound < 19) return season
  const ladder = buildLadder(season, 19)
  const finals = season.finals ?? createFinalsState(season.season, ladder, season.teams)
  return { ...season, finals }
}

export const advanceSeasonFinalsWeek = (season: SeasonState): SeasonState =>
  season.finals?.status === 'active' ? { ...season, finals: advanceFinals(season.finals, season.teams) } : season

export const prepareFinalsWeek = (state: GameState): GameState => {
  const season = state.season!
  const week = Math.max(1, Math.min(4, state.round - 19)) as 1 | 2 | 3 | 4
  const fixture = season.finals?.fixtures.find(entry => entry.week === week && !entry.result && (entry.homeTeamId === season.userTeamId || entry.awayTeamId === season.userTeamId))
  if (!fixture) return {
    ...state, phase: 'headquarters', matchResult: null, consequenceReport: null,
    opponent: { name: `Finals Week ${week}`, record: { wins: 0, losses: 0, draws: 0 }, strengths: [], weaknesses: [], style: 'AI finals progression', assessment: season.finals?.qualifiers.includes(season.userTeamId) ? 'Kingsport has no match this week. Progress the remaining finals once.' : 'Kingsport finished outside the Final Eight. Progress the AI finals once.' },
    week: { fixtureId: `s${season.season}-finals-week-${week}-ai`, venue: 'bye', staffDefaults: clone(state.staffPlan) },
  }
  const opponentId = fixture.homeTeamId === season.userTeamId ? fixture.awayTeamId : fixture.homeTeamId
  const opponent = season.teams[opponentId]
  const profile = OPPONENT_PROFILES[opponentId]
  const priorDefaults = state.week?.staffDefaults ?? state.staffPlan
  const staffChoices = profile?.staffChoices ?? state.tactics.map(tactic => tactic.staffChoice)
  const tactics = state.tactics.map((tactic, index) => ({ ...clone(tactic), staffChoice: staffChoices[index], currentChoice: staffChoices[index] }))
  const instruction = { ...clone(state.conditionalInstruction ?? state.staffPlan.conditionalInstruction), description: `Staff response tailored to ${opponent.name}.`, condition: `${opponent.name} wins repeated contests`, isSelected: true }
  const staffPlan: StaffPlan = {
    team: clone(priorDefaults.team), development: clone(priorDefaults.development), research: { decision: 'D4', points: 0 },
    training: clone(priorDefaults.training), matchPlan: clone(tactics), conditionalInstruction: clone(instruction),
  }
  return repairUnavailableSelections({
    ...state, phase: 'headquarters', matchResult: null, consequenceReport: null,
    opponent: { name: opponent.name, record: { wins: 0, losses: 0, draws: 0 }, strengths: opponent.strengths ?? [], weaknesses: opponent.weaknesses ?? [], style: opponent.style ?? 'Finals football', assessment: `${fixture.slot} · ${fixture.venue}. ${fixture.stage === 'qualifying-final' ? 'The winner earns a preliminary-final place; the loser has a double chance.' : fixture.stage === 'elimination-final' ? 'Elimination final: the loser is out.' : fixture.stage === 'grand-final' ? `Grand Final at the neutral ${fixture.venue}.` : 'Knockout final: the loser is eliminated.'}` },
    tactics,
    conditionalInstruction: instruction,
    staffPlan,
    researchPlan: createResearchPlan(state.round, opponent.name, state.club.resources.research, opponent.style, opponent.strengths, opponent.weaknesses),
    week: { fixtureId: fixture.id, venue: fixture.homeTeamId === season.userTeamId ? 'home' : 'away', staffDefaults: clone(staffPlan) },
  })
}

export const buildLadder = (season: SeasonState, throughRound = season.completedThroughRound): LadderRow[] => {
  const rows = Object.values(season.teams).map(team => ({ teamId: team.id, name: team.name, played: 0, wins: 0, losses: 0, draws: 0, pointsFor: 0, pointsAgainst: 0, percentage: 0, premiershipPoints: 0 }))
  const byId = new Map(rows.map(row => [row.teamId, row]))
  for (const result of Object.values(season.results).filter(result => result.round <= throughRound)) {
    const home = byId.get(result.homeTeamId)!
    const away = byId.get(result.awayTeamId)!
    home.played += 1; away.played += 1
    home.pointsFor += result.homeScore.total; home.pointsAgainst += result.awayScore.total
    away.pointsFor += result.awayScore.total; away.pointsAgainst += result.homeScore.total
    if (result.homeScore.total === result.awayScore.total) { home.draws += 1; away.draws += 1; home.premiershipPoints += 2; away.premiershipPoints += 2 }
    else if (result.homeScore.total > result.awayScore.total) { home.wins += 1; away.losses += 1; home.premiershipPoints += 4 }
    else { away.wins += 1; home.losses += 1; away.premiershipPoints += 4 }
  }
  for (const row of rows) row.percentage = row.pointsAgainst ? (row.pointsFor / row.pointsAgainst) * 100 : row.pointsFor ? 999 : 0
  return rows.sort((a, b) => b.premiershipPoints - a.premiershipPoints || b.percentage - a.percentage || b.pointsFor - a.pointsFor || a.teamId.localeCompare(b.teamId))
}

export const displayedClubRecord = (state: GameState) => {
  if (!state.season) return state.club.record
  const row = buildLadder(state.season, state.season.completedThroughRound).find(entry => entry.teamId === state.season!.userTeamId)
  return row ? { wins: row.wins, losses: row.losses, draws: row.draws } : state.club.record
}

export const displayedOpponentRecord = (state: GameState) => {
  if (!state.season || !state.week) return state.opponent.record
  const fixture = state.season.fixtures.find(entry => entry.id === state.week!.fixtureId)
  if (!fixture) return state.opponent.record
  const opponentId = fixture.homeTeamId === state.season.userTeamId ? fixture.awayTeamId : fixture.homeTeamId
  const row = buildLadder(state.season, state.season.completedThroughRound).find(entry => entry.teamId === opponentId)
  return row ? { wins: row.wins, losses: row.losses, draws: row.draws } : state.opponent.record
}

export const prepareSeasonRound = (rawState: GameState, round: number): GameState => {
  const state = initialiseSeason(rawState)
  const season = state.season!
  const fixture = fixtureForTeamOrBye(season, season.userTeamId, round)
  if (!fixture) return {
    ...state,
    round,
    phase: 'headquarters',
    opponent: { name: 'Bye week', record: { wins: 0, losses: 0, draws: 0 }, strengths: [], weaknesses: [], style: 'No scheduled opponent', assessment: 'Club processes continue once while the other nine matches are resolved.' },
    matchResult: null,
    consequenceReport: null,
    week: { fixtureId: `s${season.season}-r${round}-bye-${season.userTeamId}`, venue: 'bye', staffDefaults: clone(state.week?.staffDefaults ?? state.staffPlan) },
  }
  const opponentId = fixture.homeTeamId === season.userTeamId ? fixture.awayTeamId : fixture.homeTeamId
  const profile = OPPONENT_PROFILES[opponentId]
  const opponentTeam = season.teams[opponentId]
  const tactics = state.tactics.map((tactic, index) => ({ ...clone(tactic), staffChoice: profile.staffChoices[index], currentChoice: profile.staffChoices[index] }))
  const tacticalChoices = Object.fromEntries(tactics.map(tactic => [tactic.id, tactic.currentChoice]))
  const instruction = { id: 'D7-A' as const, label: profile.conditional.label, description: `Staff response tailored to ${opponentTeam.name}.`, condition: profile.conditional.condition, action: profile.conditional.action, isAvailable: true, isSelected: true }
  const priorDefaults = state.week?.staffDefaults ?? state.staffPlan
  const staffPlan: StaffPlan = {
    team: clone(priorDefaults.team),
    development: clone(priorDefaults.development),
    research: { decision: 'D4', points: 0 },
    training: clone(priorDefaults.training),
    matchPlan: clone(tactics),
    conditionalInstruction: clone(instruction),
  }
  return repairUnavailableSelections({
    ...state,
    round,
    phase: 'headquarters',
    seed: fixture.seed,
    opponent: { name: opponentTeam.name, record: { wins: 0, losses: 0, draws: 0 }, strengths: clone(profile.strengths), weaknesses: clone(profile.weaknesses), style: profile.style, assessment: profile.assessment },
    decisions: Object.fromEntries(Object.entries(state.decisions).map(([id, decision]) => [id, { ...decision, currentChoice: tacticalChoices[id] ?? 'A', isLocked: false }])) as GameState['decisions'],
    tactics,
    conditionalInstruction: instruction,
    staffPlan,
    researchPlan: createResearchPlan(round, opponentTeam.name, state.club.resources.research, profile.style, profile.strengths, profile.weaknesses),
    matchResult: null,
    consequenceReport: null,
    week: { fixtureId: fixture.id, venue: fixture.homeTeamId === season.userTeamId ? 'home' : 'away', staffDefaults: clone(staffPlan) },
  })
}
